import {
  createHost,
  getProfile,
  MakeHostPeerConfig,
  PlaycastHostPeerStatus,
  PlaycastLocalHosts,
  PlaycastPeerHost,
  PlaycastRemotePeer,
  PlaycastRemotePeerClient,
  PlaycastWebrtcPresetGroup,
  PlaycastWebrtcStats,
} from '@playcastdotio/Peer';
import { getStateStore } from '../../stores';
import { handleIncomingMessages } from '../../messaging/handlers/player';
import {
  PlaycastMessage,
  PlaycastMessageTarget,
  PeerIdType,
  HostPresence,
  websocket,
  PresenceMessageType,
  ClientType,
  PeerMessage,
  generators,
} from '@playcastdotio/Messaging';
import { addReplicationStateChangeCallback, getNotifyMessages } from '@playcastdotio/StateStore';
import { getIpcFunctions } from '../ipcInterface';
import { getTracksForProfile } from '../managers/mediaManager';
import * as realtime from '../realtime';
import constants from '@playcastdotio/GlobalConfig';
import { USE_SESSION_COORDINATOR } from '../../../config/featureFlags';
import { sessionCoordinator } from '../../../coordinators/init';
import { sendMessage } from '../realtime';
import { cleanupActivityListeners, startAFKTimeout, startInactivityTimeout } from '../managers/playerActivity';
import type { PlaycastChannels } from '@playcastdotio/CoreTypes';
import { adModeState, hostState } from '../../stores/shared';
import { sendHostMetricBatchWebrtcStats, WebrtcQualityStatsMetric } from '../realtime';

const state = getStateStore('ipc');
let ipcFunctions: ReturnType<typeof getIpcFunctions>;

/**
 * Ensure IPC functions are initialized (lazy initialization)
 *
 * TODO-STRANGLER-FIG: Remove when IPC dependencies are eliminated
 */
const ensureIpcFunctions = () => {
  if (!ipcFunctions) {
    ipcFunctions = getIpcFunctions();
  }
};

let hostPeer: PlaycastPeerHost;

/**
 * Set the host peer (used by RealPeerManager to bridge new system with legacy handlers)
 *
 * TODO-STRANGLER-FIG: Remove this when legacy peer message handlers are migrated
 */
export const setHostPeer = (peer: PlaycastPeerHost | null) => {
  hostPeer = peer as PlaycastPeerHost;
};

/**
 * Get the host peer (for debugging/testing)
 */
export const getHostPeer = (): PlaycastPeerHost | null => {
  return hostPeer || null;
};

// Per-peer stats collection
interface PeerStatsCollection {
  stats: WebrtcQualityStatsMetric[];
  metadata: {
    playerConnectionId: string;
    persistentId: string;
    playerAuthId: string;
  };
}

const peerStatsCollections = new Map<string, PeerStatsCollection>();

export const hostHandlePeerMessage = (message: PeerMessage) => {
  if (hostPeer) {
    hostPeer.handlePeerMessage(message);
  } else {
    console.error('[Host] No host peer to handle message', message);
  }
};

/**
 * Updates the hostWebrtcStatsState with the latest peer statistics
 * @param remotePeerId - The ID of the remote peer
 * @param stats - The WebRTC statistics object, or null to remove the entry
 */
const updatePeerWebrtcStats = (remotePeerId: string, stats: PlaycastWebrtcStats | null) => {
  const currentStats = state.hostWebrtcStatsState.currentState;
  const newStats = { ...currentStats };

  if (stats === null) {
    // Remove the peer from stats when disconnecting
    delete newStats[remotePeerId];
  } else {
    // Update with new stats
    newStats[remotePeerId] = stats;
  }

  state.hostWebrtcStatsState.updateState(newStats);
};

/**
 * Removes a peer from the hostWebrtcStatsState when they disconnect
 * @param remotePeerId - The ID of the remote peer to remove
 */
const removePeerWebrtcStats = (remotePeerId: string) => {
  updatePeerWebrtcStats(remotePeerId, null);
};

/**
 * Initializes stats collection for a peer
 */
export const initializePeerStatsCollection = (remotePeerId: string, peerMetadata: { playerPersistentId?: string; playerAuthId?: string }) => {
  peerStatsCollections.set(remotePeerId, {
    stats: [],
    metadata: {
      playerConnectionId: remotePeerId,
      persistentId: peerMetadata?.playerPersistentId || '',
      playerAuthId: peerMetadata?.playerAuthId || '',
    },
  });
  console.log('[Host] Initialized stats collection for peer:', remotePeerId);
};

/**
 * Adds a stats entry to a peer's collection and sends batch if full
 */
const addPeerStats = (remotePeerId: string, stats: PlaycastWebrtcStats) => {
  const collection = peerStatsCollections.get(remotePeerId);
  if (!collection) {
    console.warn('[Host] No stats collection found for peer:', remotePeerId);
    return;
  }

  // Combine quality stats with RTP timing data
  const rtpTimingData = extractRtpTimingData(stats.rtp);
  const combinedRawStats = {
    // Start with quality management stats (legacy fields)
    ...stats.quality.rawStats,
    // Add timing data from RTP stats
    ...rtpTimingData,
  };

  // Log the combined stats for debugging (only occasionally to avoid spam)
  if (collection.stats.length % 5 === 0) {
    console.log('[Host] Combined stats for peer', remotePeerId, {
      qualityKeys: Object.keys(stats.quality.rawStats),
      rtpTimingKeys: Object.keys(rtpTimingData),
      combinedKeys: Object.keys(combinedRawStats),
      sampleTimingData: rtpTimingData
    });
  }

  const statsMetric: WebrtcQualityStatsMetric = {
    recommendation: stats.quality.recommendation,
    reason: stats.quality.reason,
    rawStats: combinedRawStats,
  };

  collection.stats.push(statsMetric);

  // Send batch when we reach 10 stats (same as player)
  if (collection.stats.length >= 10) {
    sendHostStatsMessage(remotePeerId, collection);
    collection.stats = []; // Clear the collection
  }
};

/**
 * Extracts timing data from RTP stats (both inbound and outbound)
 */
const extractRtpTimingData = (rtpStats: { inbound: Array<Record<string, any>>; outbound: Array<Record<string, any>> }) => {
  const timingData: Record<string, number> = {};

  console.log('[Host] Extracting RTP timing data:', {
    outboundCount: rtpStats.outbound?.length || 0,
    inboundCount: rtpStats.inbound?.length || 0,
    outboundStreams: rtpStats.outbound?.map((stream, i) => ({
      index: i,
      ssrc: stream.ssrc,
      type: stream.type,
      hasEncodeTime: stream.averageEncodeTimePerFrameMs !== undefined,
      hasFramesEncoded: stream.framesEncoded !== undefined,
      avgEncodeTime: stream.averageEncodeTimePerFrameMs,
      totalEncodeTime: stream.totalEncodeTime,
      framesEncoded: stream.framesEncoded,
      codecName: stream.codec?.name,
      frameWidth: stream.frameWidth,
      frameHeight: stream.frameHeight
    })) || []
  });

  // Extract from outbound (encoding) stats - host perspective
  // Loop through outbound streams to find the one with encoding data
  if (rtpStats.outbound && rtpStats.outbound.length > 0) {
    let foundEncodingData = false;

    for (let i = 0; i < rtpStats.outbound.length; i++) {
      const outbound = rtpStats.outbound[i];
      console.log(`[Host] Checking outbound stream ${i}:`, {
        ssrc: outbound.ssrc,
        averageEncodeTimePerFrameMs: outbound.averageEncodeTimePerFrameMs,
        totalEncodeTime: outbound.totalEncodeTime,
        framesEncoded: outbound.framesEncoded,
        frameWidth: outbound.frameWidth,
        frameHeight: outbound.frameHeight,
        hasEncodingData: (outbound.averageEncodeTimePerFrameMs && outbound.averageEncodeTimePerFrameMs > 0) ||
                        (outbound.totalEncodeTime && outbound.totalEncodeTime > 0) ||
                        (outbound.framesEncoded && outbound.framesEncoded > 0)
      });

      // Check if this stream has meaningful encoding timing data (greater than 0)
      if ((outbound.averageEncodeTimePerFrameMs && outbound.averageEncodeTimePerFrameMs > 0) ||
          (outbound.totalEncodeTime && outbound.totalEncodeTime > 0) ||
          (outbound.framesEncoded && outbound.framesEncoded > 0)) {

        timingData.averageEncodeTimePerFrameMs = outbound.averageEncodeTimePerFrameMs || 0;
        timingData.totalEncodeTime = outbound.totalEncodeTime || 0;
        timingData.framesEncoded = outbound.framesEncoded || 0;
        foundEncodingData = true;
        console.log(`[Host] ✅ Found encoding data in stream ${i}:`, {
          averageEncodeTimePerFrameMs: timingData.averageEncodeTimePerFrameMs,
          totalEncodeTime: timingData.totalEncodeTime,
          framesEncoded: timingData.framesEncoded
        });
        break; // Found the stream with encoding data, use it
      }
    }

    // If no stream had encoding data, set defaults
    if (!foundEncodingData) {
      timingData.averageEncodeTimePerFrameMs = 0;
      timingData.totalEncodeTime = 0;
      timingData.framesEncoded = 0;
      console.log('[Host] ❌ No encoding data found in any outbound stream, using defaults');
    }
  }

  // Extract from inbound (decoding) stats - for completeness
  // Loop through inbound streams to find the one with decoding data
  if (rtpStats.inbound && rtpStats.inbound.length > 0) {
    let foundDecodingData = false;

    for (const inbound of rtpStats.inbound) {
      // Check if this stream has meaningful decoding timing data (greater than 0)
      if ((inbound.averageDecodeTimePerFrameMs && inbound.averageDecodeTimePerFrameMs > 0) ||
          (inbound.averageProcessingDelayPerFrameMs && inbound.averageProcessingDelayPerFrameMs > 0) ||
          (inbound.totalDecodeTime && inbound.totalDecodeTime > 0) ||
          (inbound.framesDecoded && inbound.framesDecoded > 0)) {

        timingData.averageDecodeTimePerFrameMs = inbound.averageDecodeTimePerFrameMs || 0;
        timingData.averageProcessingDelayPerFrameMs = inbound.averageProcessingDelayPerFrameMs || 0;
        timingData.totalDecodeTime = inbound.totalDecodeTime || 0;
        timingData.totalProcessingDelay = inbound.totalProcessingDelay || 0;
        timingData.framesDecoded = inbound.framesDecoded || 0;
        timingData.jitterBufferEmittedCount = inbound.jitterBufferEmittedCount || 0;
        foundDecodingData = true;
        break; // Found the stream with decoding data, use it
      }
    }

    // If no stream had decoding data, set defaults
    if (!foundDecodingData) {
      timingData.averageDecodeTimePerFrameMs = 0;
      timingData.averageProcessingDelayPerFrameMs = 0;
      timingData.totalDecodeTime = 0;
      timingData.totalProcessingDelay = 0;
      timingData.framesDecoded = 0;
      timingData.jitterBufferEmittedCount = 0;
    }
  }

  return timingData;
};

/**
 * Sends host stats message to realtime API
 */
const sendHostStatsMessage = (remotePeerId: string, collection: PeerStatsCollection) => {
  const peerMetadata = {
    ...collection.metadata,
    broadcastSessionId: state.hostPresenceState.currentState.hostPeerId || '',
    inviteId: state.invitationStateV2.currentState.id || '',
  };

  console.log('[Host] Sending stats batch for peer:', remotePeerId, 'stats count:', collection.stats.length);
  sendHostMetricBatchWebrtcStats(collection.stats, 1000, peerMetadata);
};

/**
 * Cleans up stats collection for a disconnected peer (with final batch send)
 */
export const cleanupPeerStatsCollection = (remotePeerId: string) => {
  const collection = peerStatsCollections.get(remotePeerId);
  if (collection && collection.stats.length > 0) {
    // Send any remaining stats before cleanup
    console.log('[Host] Sending final stats batch for disconnecting peer:', remotePeerId);
    sendHostStatsMessage(remotePeerId, collection);
  }
  peerStatsCollections.delete(remotePeerId);
  console.log('[Host] Cleaned up stats collection for peer:', remotePeerId);
};

const setupHostPeer = (hostPeerId: string) => {
  const state = getStateStore('ipc');
  const iceServers = state.iceServerState.currentState.hostIceServers;
  hostPeer = createHost(
    {
      validatorFunction,
      realtimeApiManager: {
        send: (message) => {
          sendMessage(message);
        },
      },
      iceServers: iceServers || undefined,
      forceTurn: !state.iceServerState.currentState.allowPeerConnections,
    },
    hostPeerId,
  );
  let cachedHostPeerStatus = hostPeer.status.currentState;
  let cachedPeerIds = Object.keys(hostPeer.remotePeers.currentState);

  // Set up quality preset listener
  hostPeer.qualityPresetStore.addListener((presetGroup) => {
    if (presetGroup) {
      state.webrtcPresetGroups.updateState(presetGroup);
    }
  });
  state.webrtcPresetGroups.updateState(hostPeer.qualityPresetStore.currentState);

  const handleStatusChange = async (status: PlaycastHostPeerStatus) => {
    console.debug('[Host] Status changed', status);
    cachedHostPeerStatus = status;
    if (['closed', 'failed', 'disconnected'].includes(status.connectionState)) {
      state.hostState.updateState({
        ...state.hostState.currentState,
        status: 'error',
        statusMessage: 'Host connection failed',
        startTime: 0,
        streamQrImageBase64: '',
      });
    } else if (status.connectionState === 'listening') {
      // Wait for the invitation to update, if it doesnt already have an id.
      await new Promise<void>((resolve) => {
        if (state.invitationStateV2.currentState.id) {
          resolve();
        } else {
          const listener = state.invitationStateV2.addListener((invitation) => {
            if (invitation.id) {
              resolve();
              state.invitationStateV2.removeListener(listener);
            }
          });
          // Set a timeout to resolve if the invitation doesnt update in time
          setTimeout(() => {
            resolve();
          }, 1000);
        }
      });

      // STRANGLER-FIG: Skip PRESENCE_UPDATE when SessionCoordinator is active
      if (!USE_SESSION_COORDINATOR) {
        sendMessage(
          websocket.generators.presence(PresenceMessageType.PRESENCE_UPDATE, {
            clientType: ClientType.HOST,
            presence: { ...state.hostPresenceState.currentState, status: 'hosting', playerCount: 0 },
          }),
        );
      }
      state.hostPresenceState.updateState({
        ...state.hostPresenceState.currentState,
        status: 'hosting',
      });
      state.hostState.updateState({
        ...state.hostState.currentState,
        status: 'streaming',
        statusMessage: 'Stream is live! ID is ' + state.invitationStateV2.currentState.id,
        startTime: Date.now(),
      });
    }
  };

  // Set up status listener
  hostPeer.status.addListener((status) => {
    if (cachedHostPeerStatus === status) return;
    handleStatusChange(status);
  });
  const currentStatus = hostPeer.status.currentState;
  handleStatusChange(currentStatus);

  // Set up remote peers listener
  hostPeer.remotePeers.addListener((peers) => {
    const addedPeers = Object.keys(peers).filter((peer) => !cachedPeerIds.includes(peer));
    const removedPeers = cachedPeerIds.filter((peer) => !Object.keys(peers).includes(peer));
    cachedPeerIds = Object.keys(peers);

    // Handle added peers
    addedPeers.forEach((peerId) => {
      console.debug('[Host] Adding peer', peerId);
      const peer = peers[peerId] as PlaycastRemotePeer;
      if (peer.status.currentState.connectionState === 'connected') {
        initializePlayer(peer as PlaycastRemotePeerClient);
      }
    });

    // Handle removed peers
    removedPeers.forEach((peerId) => {
      console.debug('[Host] Removing peer', peerId);

      // Clean up stats collection (sends final batch if needed)
      cleanupPeerStatsCollection(peerId);

      const playerState = structuredClone(state.playerState.currentState.players);
      delete playerState[peerId];
      state.playerState.updateState({ players: playerState });

      cleanupActivityListeners(peerId);
      const playerActivityState = structuredClone(state.playerActivityState.currentState.players);
      delete playerActivityState[peerId];
      state.playerActivityState.updateState({
        inactive: state.playerActivityState.currentState.inactive,
        players: { ...playerActivityState },
      });

      const playerStatState = structuredClone(state.playerStatState.currentState.players);
      delete playerStatState[peerId];
      state.playerStatState.updateState({ players: { ...playerStatState } });

      // Remove peer from hostWebrtcStatsState
      removePeerWebrtcStats(peerId);
    });
  });
};

export const startStream = async () => {
  ensureIpcFunctions();

  if (state.hostState.currentState.status !== 'idle' && state.hostState.currentState.status !== 'error') {
    console.error('[Host] Already streaming!');
    return;
  }

  // Update state to starting
  state.hostState.updateState({
    ...state.hostState.currentState,
    status: 'starting',
    statusMessage: 'Starting stream...',
    startTime: 0,
  });

  try {
    console.warn('[Host] Starting stream with realtime API');

    // Check if we need to close an existing peer first
    if (hostPeer) {
      console.log('[Host] Closing existing host peer before creating a new one');
      hostPeer.close('host:close:duplicate_peer');
    }

    // Request peer ID from realtime API using the new function
    realtime.requestPeerId(PeerIdType.HOST);

    // Wait for peer ID response
    const hostPeerId = await new Promise<string>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Timeout waiting for peer ID'));
      }, 10000);

      // Check if we already have a peer ID
      if (state.hostPresenceState.currentState.hostPeerId) {
        clearTimeout(timeout);
        return resolve(state.hostPresenceState.currentState.hostPeerId);
      }

      const listener = state.hostPresenceState.addListener((presence) => {
        if (presence.hostPeerId) {
          clearTimeout(timeout);
          state.hostPresenceState.removeListener(listener);
          resolve(presence.hostPeerId);
        }
      });
    });

    console.log('[Host] Received peer ID, setting up host peer:', hostPeerId);
    setupHostPeer(hostPeerId);

    // STRANGLER-FIG: Notify SessionCoordinator that stream started
    if (USE_SESSION_COORDINATOR) {
      sessionCoordinator.onStreamStarted(hostPeerId);
    }

    // Should update this once the peer is actually up and running, established ice etc.
    // await realtime.sendPresenceUpdate({
    //   status: 'hosting',
    //   playerCount: 0,
    //   maxPlayerCount: state.hostPresenceState.currentState.maxPlayerCount,
    // });
    // state.hostPresenceState.updateState({
    //   ...state.hostPresenceState.currentState,
    //   status: 'hosting',
    // });
  } catch (error) {
    console.error('[Host] Failed to start stream:', error);
    state.qrCodeBase64State.updateState('');
    state.hostState.updateState({
      ...state.hostState.currentState,
      status: 'error',
      statusMessage: error instanceof Error ? error.message : 'Failed to start stream',
      startTime: 0,
      streamQrImageBase64: '',
    });
  }
};

addReplicationStateChangeCallback('player', (msg, splitReplicationKey) => {
  if (splitReplicationKey) {
    sendMessageToPlayer(splitReplicationKey, msg);
  } else {
    broadcastMessage(msg);
  }
});

export const stopStream = async (reason?: string) => {
  // Disconnect all players when stopping stream
  // This ensures playerCount drops to 0, triggering Ad Mode restart if needed
  const playerIds = Object.keys(state.playerState.currentState.players);
  if (playerIds.length > 0) {
    console.log('[Host] Disconnecting all players on stream stop:', playerIds);
    playerIds.forEach((playerId) => {
      kickPlayer(playerId, reason ?? 'stream_stopped');
    });
  }

  state.hostState.updateState({
    ...state.hostState.currentState,
    status: 'stopping',
  });
  if (state.hostPresenceState.currentState.hostPeerId === '') {
    console.warn('[Host] No stream to stop (hostPeerId empty)');

    // BUG FIX: Even if hostPeerId is empty, we must send a presence update to the API
    // to ensure the host status is properly updated to 'idle'. Without this, the API
    // might show the host as 'hosting' with 0 players (stuck state).
    const earlyUpdate: HostPresence = {
      ...state.hostPresenceState.currentState,
      status: 'idle',
      playerCount: 0,
      runningGame: undefined,
      hostPeerId: '',
    };
    // STRANGLER-FIG: Skip PRESENCE_UPDATE when SessionCoordinator is active
    if (!USE_SESSION_COORDINATOR) {
      sendMessage(
        websocket.generators.presence(PresenceMessageType.PRESENCE_UPDATE, {
          clientType: ClientType.HOST,
          presence: earlyUpdate,
        }),
      );
    }
    state.hostPresenceState.updateState(earlyUpdate);

    state.hostState.updateState({
      ...state.hostState.currentState,
      status: 'idle',
      statusMessage: 'No stream to stop',
      startTime: 0,
      streamQrImageBase64: '',
    });
    state.qrCodeBase64State.updateState('');
    if (hostPeer) {
      hostPeer.close(reason ?? 'host:close:stop_stream');
    }

    // Clear the peer ID from memory to force a new one on next stream start
    realtime.clearPeerData(PeerIdType.HOST);

    // STRANGLER-FIG: Notify SessionCoordinator that stream stopped (early exit path)
    if (USE_SESSION_COORDINATOR) {
      sessionCoordinator.onStreamStopped(reason || 'no_peer_id');
    }

    return;
  }
  const update: HostPresence = {
    ...state.hostPresenceState.currentState,
    status: 'idle',
    playerCount: 0,
    runningGame: undefined,
    hostPeerId: '',
  };
  // STRANGLER-FIG: Skip PRESENCE_UPDATE when SessionCoordinator is active
  if (!USE_SESSION_COORDINATOR) {
    sendMessage(
      websocket.generators.presence(PresenceMessageType.PRESENCE_UPDATE, {
        clientType: ClientType.HOST,
        presence: update,
      }),
    );
  }
  state.hostPresenceState.updateState(update);

  state.hostState.updateState({
    ...state.hostState.currentState,
    status: 'stopping',
    statusMessage: 'Stream stopped',
    startTime: 0,
    streamQrImageBase64: '',
  });
  state.qrCodeBase64State.updateState('');
  hostPeer.close(reason ?? 'host:close:stop_stream');
  state.hostState.updateState({
    ...state.hostState.currentState,
    status: 'idle',
    statusMessage: 'Stream stopped',
    startTime: 0,
    streamQrImageBase64: '',
  });
  state.qrCodeBase64State.updateState('');

  // Clear the peer ID from memory to force a new one on next stream start
  realtime.clearPeerData(PeerIdType.HOST);

  // STRANGLER-FIG: Notify SessionCoordinator that stream stopped
  if (USE_SESSION_COORDINATOR) {
    sessionCoordinator.onStreamStopped(reason || 'user_requested');
  }
};

export const kickPlayer = (peerId: string, reason?: string) => {
  if (hostPeer) {
    const maybeRemotePeer = hostPeer.remotePeers.currentState[peerId];
    if (maybeRemotePeer) {
      maybeRemotePeer.disconnect(false, reason ?? 'kicked');
    } else {
      console.error('[Host] Could not find remote peer to kick', peerId);
    }
  }
};

export const checkPlayerIds = (invitationPlayerIds: string[], removedPlayerIds: string[]) => {
  if (hostPeer) {
    console.log('[Host] Checking player IDs', invitationPlayerIds);

    Object.values(hostPeer.remotePeers.currentState).forEach((peer) => {
      const metadata = peer.getMetaData();
      const peerInvitationPlayerId = metadata?.invitationPlayerId || '';
      const peerAuthId = metadata?.playerAuthId || '';
      const peerPersistentId = metadata?.playerPersistentId || '';
      if (
        removedPlayerIds.includes(peerInvitationPlayerId) ||
        removedPlayerIds.includes(peerAuthId) ||
        removedPlayerIds.includes(peerPersistentId)
      ) {
        peer.disconnect(false, 'kicked, not in accepted list, removed from invitation');
      }
    });

    return; // early return for now
    Object.values(hostPeer.remotePeers.currentState).forEach((peer) => {
      const metadata = peer.getMetaData();
      const peerInvitationPlayerId = metadata?.invitationPlayerId || '';
      const peerAuthId = metadata?.playerAuthId || '';
      const peerPersistentId = metadata?.playerPersistentId || '';

      // Check if any of the IDs match the invitation player IDs
      const hasMatch =
        invitationPlayerIds.includes(peerInvitationPlayerId) ||
        invitationPlayerIds.includes(peerAuthId) ||
        invitationPlayerIds.includes(peerPersistentId);

      // If none of the IDs match, disconnect the peer
      if (!hasMatch) {
        peer.disconnect(false, 'kicked, not in accepted list');
      }
    });
  }
};
export const sendMessageToPlayer = (playerId: string, message: PlaycastMessage<PlaycastMessageTarget>) => {
  const player = hostPeer?.remotePeers.currentState[playerId];
  if (player) {
    player.sendMessage(message);
  }
};
export const broadcastMessage = (message: PlaycastMessage<PlaycastMessageTarget>) => {
  hostPeer?.broadcastMessage(message);
};

export const setPresetGroup = (presetGroup: PlaycastWebrtcPresetGroup) => {
  hostPeer?.setQualityPresetGroup(presetGroup);
};

export const setShouldHandlePanics = (shouldHandlePanics: boolean) => {
  hostPeer?.setShouldHandlePanics(shouldHandlePanics);
  if (hostPeer) {
    console.log('[Host] Setting should handle panics', shouldHandlePanics);
  }
};

// export const setVideo = (peerId: string) => {
//   const peer = hostPeer?.remotePeers.currentState[peerId];
//   if (peer) {
//     const showVideo = state.playerState.currentState.players[peerId].showVideo;
//     if (showVideo) {

//     } else {
//       Object.values(peer.outgoingTracks.currentState).forEach((track) => {
//         if (track.metadata.type === 'video') {
//           peer.removeTrack(track.track);
//         }
//       });
//     }
//   }
// };

// export const setDesktopAudio = (track: MediaStreamTrack, peerId?: string) => {
//   if (peerId) {
//     const peer = hostPeer?.remotePeers.currentState[peerId];
//     if (peer) {
//       peer.addTrack(track, { channel: 'hostDesktopAudio', displayName: 'Host Desktop Audio', type: 'audio' });
//     }
//   } else {
//     hostPeer.defaultTracks.currentState['hostDesktopAudio'] = {
//       track,
//       metadata: {
//         channel: 'hostDesktopAudio',
//         displayName: 'Host Desktop Audio',
//         type: 'audio',
//       },
//     };
//     hostPeer.defaultTracks.updateState({ ...hostPeer.defaultTracks.currentState });
//   }
// };

// Track which players already have timeouts
const timeoutPlayers = new Set();
const clearKnocks = () => {
  const knocks = state.lobbyStateV2.currentState.knocks;
  if (!knocks?.accepted) return;

  // Create timeouts for each accepted player that doesn't already have one
  knocks.accepted.forEach((player) => {
    if (!timeoutPlayers.has(player.playerId)) {
      timeoutPlayers.add(player.playerId);
      setTimeout(() => {
        // After 30 seconds, remove the player if they're still in accepted list
        timeoutPlayers.delete(player.playerId);
        state.lobbyStateV2.updateState({
          ...state.lobbyStateV2.currentState,
          knocks: {
            ...state.lobbyStateV2.currentState.knocks,
            accepted: state.lobbyStateV2.currentState.knocks.accepted.filter((p) => p.playerId !== player.playerId),
          },
        });
      }, 30000);
    }
  });
};

export const validatorFunction = async (localPeer: PlaycastLocalHosts, remotePeer: PlaycastRemotePeer): Promise<boolean> => {
  // const remoteMetadata = remotePeer.getMetaData();
  // if (hostState.currentState.edition === 'pavilion') {
  //   // Bypass for now...
  //   return true;
  // }
  // const playerId = remoteMetadata?.invitationPlayerId;

  // if (!playerId) {
  //   console.error('[Host] Player not authorized', remotePeer.remotePeerId);
  //   return false;
  // }

  // const knocks = state.lobbyStateV2.currentState.knocks;
  // if (!knocks) {
  //   console.error('[Host] No knocks found in lobby state');
  //   return false;
  // }

  // // check if the player is in the accepted list
  // const player = knocks.accepted.find((p) => p.playerId === playerId);
  // if (!player) {
  //   console.error('[Host] Player not authorized', remotePeer.remotePeerId);
  //   return false;
  // }

  // // remove the player from the accepted list
  // knocks.accepted = knocks.accepted.filter((p) => p.playerId !== playerId);

  clearKnocks();

  return true;
};

export const initializePlayer = (peer: PlaycastRemotePeerClient) => {
  console.debug('[Host] Adding player', peer.remotePeerId);

  // Initialize stats collection for this peer
  initializePeerStatsCollection(peer.remotePeerId, peer.getMetaData());

  peer.onMessage((msg) => {
    handleIncomingMessages(peer.remotePeerId, msg);
  });
  // check if there are any free assignments.
  // get all the assignments
  // const allAssignments = Object.values(state.gamepadState.currentState.playerMappings);
  // const allMapped = allAssignments.map((assignment) => assignment?.gamepadIndex);
  // const filtered = allMapped.filter((index) => index !== -1);
  // const available = Array.from(Array(MAX_GAMEPAD_SLOTS).keys()).filter((index) => !filtered.includes(index as PlaycastGamepadId));

  // const gamepadAccess = anyAssignmentsLeft();
  const gamepadAccess = state.gamepadState.currentState.autoAssign;

  const pavilionOwner =
    hostState.currentState.edition === 'pavilion' &&
    peer.getMetaData()?.playerAuthId === state.invitationStateV2.currentState.owner.profileId;

  const adModeEnabled = adModeState.currentState.enabled;

  // Check if touch is enabled for this machine (from Ctrl+F12 debug menu)
  const touchEnabled = state.touchInputConfigState.currentState.enabled;

  const playerState = structuredClone(state.playerState.currentState.players);
  playerState[peer.remotePeerId] = {
    persistentId: peer.getMetaData()?.playerPersistentId || '',
    displayName: peer.getMetaData()?.displayName || 'Anonymous',
    playerAuthId: peer.getMetaData()?.playerAuthId || '',
    invitationPlayerId: peer.getMetaData()?.invitationPlayerId || '',
    access: {
      ...state.globalInputState.currentState,
      gamepad: gamepadAccess,
      keyboard: state.globalInputState.currentState.keyboard  || pavilionOwner || adModeEnabled,
      mouse: state.globalInputState.currentState.mouse || pavilionOwner || adModeEnabled,
      touch: touchEnabled && (state.globalInputState.currentState.touch || pavilionOwner || adModeEnabled),
      chat: true,
    },
    email: peer.getMetaData()?.email || '',
    showVideo: state.videoState.currentState.enabled,
    joinTime: Date.now(),
    connectionType: peer.connectionType.currentState,
  };
  const peerProfileState = peer.profileState;
  const isPeerMobileDevice = peer.getMetaData()?.isMobileDevice || false;
  peerProfileState.updateState({ profile: isPeerMobileDevice ? 'hostLow' : 'hostMedium' });
  // delay setting tracks until connection is stable
  setTimeout(() => {
    const tracks = getTracksForProfile(getProfile(peerProfileState.currentState.profile));
    peer.setTracks(tracks);
  }, 1000);
  peerProfileState.addListener((state) => {
    const tracks = getTracksForProfile(getProfile(state.profile));
    peer.setTracks(tracks);
  });

  state.playerState.updateState({ players: playerState });

  // Player activity state initialization
  const playerActivityState = state.playerActivityState.currentState.players;
  playerActivityState[peer.remotePeerId] = {
    afk: false,
    mouse: { hasSentInput: false, hasRecentInput: false },
    keyboard: { hasSentInput: false, hasRecentInput: false },
    gamepad: {
      0: { hasSentInput: false, hasRecentInput: false },
      1: { hasSentInput: false, hasRecentInput: false },
      2: { hasSentInput: false, hasRecentInput: false },
      3: { hasSentInput: false, hasRecentInput: false },
    },
  };
  state.playerActivityState.updateState({ inactive: false, players: { ...playerActivityState } });
  if (Object.values(playerState[peer.remotePeerId].access).some((access) => access)) startAFKTimeout(peer.remotePeerId);

  // Ensure we set players default states on splitReplicated stores.
  // STRANGLER-FIG: ipcFunctions may not be initialized if using new coordinator system
  // TODO: Remove these IPC calls when fully migrated to coordinators
  if (ipcFunctions) {
    ipcFunctions.setQrCodeParameters(state.qrCodeState.currentState.defaultPlayerState, peer.remotePeerId);
    ipcFunctions.setStaticImage(state.staticImageState.currentState.staticImageBase64, peer.remotePeerId);
    // ipcFunctions.showVideo(state.videoState.currentState.enabled, peer.remotePeerId);
  }

  const notifyMessages = getNotifyMessages(['player']);
  notifyMessages.player.forEach((message) => {
    peer.sendMessage(message);
  });
  peer.connectionType.addListener((type) => {
    if (playerState[peer.remotePeerId]) {
      const newPlayerState = structuredClone(playerState);
      newPlayerState[peer.remotePeerId].connectionType = type;
      state.playerState.updateState({ players: newPlayerState });
    }
  });
  peer.statistics.addListener((stats) => {
    // get rtt
    const rtt = stats?.candidatePair?.currentRoundTripTime;
    if (rtt) {
      // Deep clone the players object
      const newPlayers = structuredClone(state.playerStatState.currentState.players);

      if (!newPlayers[peer.remotePeerId]) {
        newPlayers[peer.remotePeerId] = { rtt };
      } else {
        newPlayers[peer.remotePeerId].rtt = rtt;
      }

      state.playerStatState.updateState({
        ...state.playerStatState.currentState,
        players: newPlayers,
      });
    }

    // Update hostWebrtcStatsState with full stats
    updatePeerWebrtcStats(peer.remotePeerId, stats);

    // Add to per-peer stats collection for realtime metrics (only if stats is not null)
    if (stats) {
      addPeerStats(peer.remotePeerId, stats);
    }
  });
};

const sendEncodeTimingData = (encodeTime: number, peerId: string) => {
  const baseMessage = {
    header: {
      source: 'host' as const,
      target: 'system' as const,
      action: 'videoEncodeTiming' as const,
      isReply: false,
      hostId: state.authState.currentState.hostAuthId,
      userAuth: state.authState.currentState.hostAuthId,
    },
    body: {
      message: {},
      timings: [],
      stats: [],
      echo: false,
    },
    signature: '',
  } as any;

  const messageWithTiming = generators.addTiming(
    baseMessage,
    {
      timestamp: performance.now(),
      description: 'video_encode_time',
    },
    'host',
    {
      peer_id: peerId,
      encode_time_ms: (Math.round(encodeTime * 100) / 100).toString(),
      encode_start_time: (performance.now() - encodeTime).toString(),
      encode_end_time: performance.now().toString(),
    }
  );

  console.log('[Host] Encode timing data:', {
    peerId,
    encodeTime: Math.round(encodeTime * 100) / 100,
    timestamp: Date.now(),
    timingMessage: messageWithTiming,
  });

  // TODO: send timing data to BigQuery
};

const sendVideoTransitTimingData = (peerId: string, stage: 'host_send' | 'player_receive') => {
  const baseMessage = {
    header: {
      source: 'host' as const,
      target: 'system' as const,
      action: 'videoTransitTiming' as const,
      isReply: false,
      hostId: state.authState.currentState.hostAuthId,
      userAuth: state.authState.currentState.hostAuthId,
    },
    body: {
      message: {},
      timings: [],
      stats: [],
      echo: false,
    },
    signature: '',
  } as any;

  const messageWithTiming = generators.addTiming(
    baseMessage,
    {
      timestamp: performance.now(),
      description: `video_transit_${stage}`,
    },
    'host',
    {
      peer_id: peerId,
      stage: stage,
      transit_timestamp: performance.now().toString(),
    }
  );

  console.log('[Host] Video transit timing data:', {
    peerId,
    stage,
    timestamp: Date.now(),
    timingMessage: messageWithTiming,
  });

  // TODO: send timing data to BigQuery
};

export const notifyTracksChanged = () => {
  if (!hostPeer) {
    return;
  }
  console.warn('[Host] Notifying tracks changed');
  Object.values(hostPeer.remotePeers.currentState).forEach((peer) => {
    const newTracks = getTracksForProfile(getProfile(peer.profileState.currentState.profile));
    const currentTracks = peer.outgoingTracks.currentState;

    // Compare track IDs to see if anything actually changed
    const newTrackIds = Object.keys(newTracks).sort();
    const currentTrackIds = Object.keys(currentTracks).sort();

    const tracksChanged =
      newTrackIds.length !== currentTrackIds.length ||
      newTrackIds.some((id, index) => id !== currentTrackIds[index]) ||
      newTrackIds.some((id) => newTracks[id]?.track.id !== currentTracks[id]?.track.id);

      if (tracksChanged) {
        const encodeStartTime = performance.now();

        peer.setTracks(newTracks);

        const encodeEndTime = performance.now();
        const encodeTime = encodeEndTime - encodeStartTime;

        console.log('[Host] Sending tracks to players (tracks changed)', newTracks);
        console.log(`[Host] Video encode time: ${Math.round(encodeTime * 100) / 100}ms`);

        sendEncodeTimingData(encodeTime, peer.remotePeerId);

        sendVideoTransitTimingData(peer.remotePeerId, 'host_send');
      } else {
        console.log('[Host] Tracks unchanged, skipping setTracks call');
      }
  });
};

// export const setTracks = (tracks: Tracks, peerId?: string) => {
//   if (peerId) {
//     const peer = hostPeer?.remotePeers.currentState[peerId];
//     if (!peer) {
//       console.error('[Host] Could not find remote peer to set tracks', peerId);
//       return;
//     }
//     peer.setTracks(tracks);
//   } else {
//     hostPeer?.setTracksForAll(tracks);
//     hostPeer?.setDefaultTracks(tracks);
//   }
// };

export const enableTracksByChannel = (channel: PlaycastChannels, enable: boolean, peerId?: string) => {
  return;
  if (!hostPeer) {
    console.warn('[Host] No host peer, cannot enable/disable tracks');
    return;
  }
  const tracks = Object.keys(hostPeer?.remotePeers.currentState);
  if (!tracks) {
    console.warn('[Host] No tracks to enable/disable');
    return;
  }
  const trackIds = Object.keys(tracks).filter((trackId) => tracks[trackId].metadata.channel === channel);
  const filteredTracks = trackIds.map((trackId) => {
    return {
      track: tracks[trackId].track,
      metadata: tracks[trackId].metadata,
    };
  });
  filteredTracks.forEach((track) => {
    if (enable) {
      console.warn('[Host] Enabling track', track.metadata.displayName);
      track.track.enabled = true;
    } else {
      console.warn('[Host] Disabling track', track.metadata.displayName);
      track.track.enabled = false;
    }
  });
};

export const updateConfig = (config: Partial<MakeHostPeerConfig>) => {
  if (!hostPeer) {
    console.warn('[Host] No host peer, cannot update config');
    return;
  }
  hostPeer.updateConfig(config);
};
